import React from 'react';
import { array, func, number } from 'prop-types';
import BEMHelper from '@ynap/bem-helper';

export { Dials } from './Dials';
export { Labels } from './Labels';
export { Track } from './Track';
export { Rail } from './Rail';

import { calculateNewRange, percentToDomainValue, rangeAsValueToPercent, simpleThrottle, isPriceReset } from './utils';

export const DIAL_MIN_ID = 'dial-min';
export const DIAL_MAX_ID = 'dial-max';
export const TRACK_ID = 'track';

export const DEFAULT_STEP = 1;
export const DEFAULT_RANGE_MIN = 0;
export const DEFAULT_RANGE_MAX = 100;

export const bem = new BEMHelper('MultiRangeSlider');

export default class MultiRangeSlider extends React.Component {
    constructor(props) {
        super(props);

        const { currentRange, domain } = props;
        const rangeAsPercent = rangeAsValueToPercent(currentRange, domain);

        this.state = {
            container: undefined,
            currentDial: null,
            dragging: false,
            isTouch: false,
            rangeAsPercent: rangeAsPercent || [DEFAULT_RANGE_MIN, DEFAULT_RANGE_MAX],
            rangeAsValue: currentRange || [DEFAULT_RANGE_MIN, DEFAULT_RANGE_MAX],
        };
        this.throttledupdateRange = simpleThrottle(this.updateRange, 100).bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.currentRange[0] !== this.props.currentRange[0] || prevProps.currentRange[1] !== this.props.currentRange[1]) {
            const [minDomain, maxDomain] = this.props.domain;
            const [min, max] = this.props.currentRange;
            //should update domain
            if (min === minDomain && (max === maxDomain || (max < maxDomain && this.state.rangeAsPercent[1] === 100))) {
                this.setState({ rangeAsPercent: [0, 100], rangeAsValue: this.props.currentRange });
                this.props.updateDomain(this.props.currentRange);
            } else if (min < minDomain || max > maxDomain || isPriceReset()) {
                this.setState({ rangeAsPercent: [0, 100], rangeAsValue: this.props.currentRange });
                if (this.props.updateDomain) {
                    this.props.updateDomain(this.props.currentRange);
                }
            }
        }
    }

    componentWillUnmount() {
        this.removeMouseHandlers();
    }

    addMouseHandlers = () => {
        if (!this.state.currentDial) {
            return;
        }
        document.addEventListener('mousemove', this.dragMove);
        document.addEventListener('mouseup', this.dragStop);
        document.addEventListener('mouseleave', this.dragStop);
        document.addEventListener('touchmove', this.dragMove);
        document.addEventListener('touchend', this.dragStop);
    };

    removeMouseHandlers = () => {
        document.removeEventListener('mousemove', this.dragMove);
        document.removeEventListener('mouseup', this.dragStop);
        document.removeEventListener('mouseleave', this.dragStop);
        document.removeEventListener('touchmove', this.dragMove);
        document.removeEventListener('touchend', this.dragStop);
    };

    refCallback = (element) => {
        if (element) {
            const container = element;

            this.setState({ container });
        }
    };

    dragStart = (e, { isTouch, handleID }) => {
        if (!isTouch) {
            e.preventDefault();
        }
        this.throttledupdateRange(e, true);
        this.setState({ currentDial: handleID, dragging: true, isTouch }, this.addMouseHandlers);
    };

    dragStop = () => {
        const { rangeAsValue } = this.state;
        this.props.onChange(rangeAsValue);
        this.removeMouseHandlers();
        this.setState({ currentDial: null, dragging: null, isTouch: false });
    };

    dragMove = (e) => {
        if (!this.state.dragging) {
            return this.dragStop();
        }
        this.throttledupdateRange(e);
    };

    updateRange = (e, triggerChangeOnStart) => {
        const { container, currentDial, rangeAsPercent, isTouch } = this.state;
        const { domain, onChange, step } = this.props;
        const { left, width } = container.getBoundingClientRect();
        const targetPercent = isTouch ? Math.ceil((100 * (e.touches[0].pageX - left)) / width) : Math.ceil((100 * (e.clientX - left)) / width);

        const newRangeAsPercent = calculateNewRange({ targetPercent, rangeAsPercent, currentDial, domain, step: step });
        const newRangeAsDomainValue = [percentToDomainValue(newRangeAsPercent[0], domain), percentToDomainValue(newRangeAsPercent[1], domain)];

        if (triggerChangeOnStart && currentDial === TRACK_ID) {
            onChange(newRangeAsDomainValue);
        }

        this.setState({ rangeAsPercent: newRangeAsPercent, rangeAsValue: newRangeAsDomainValue });
    };

    render() {
        const { container, rangeAsPercent, rangeAsValue } = this.state;

        const derivedProps = {
            onMouseDown: this.dragStart,
            onTouchStart: this.dragStart,
            container,
            rangeAsPercent,
            rangeAsValue,
        };

        return (
            <div className={bem()}>
                <div ref={this.refCallback} className={bem('Component')}>
                    {React.Children.map(this.props.children, (node) => React.cloneElement(node, derivedProps))}
                </div>
            </div>
        );
    }
}

MultiRangeSlider.propTypes = {
    currentRange: array,
    domain: array,
    onChange: func,
    step: number,
};

MultiRangeSlider.defaultProps = {
    currentRange: [DEFAULT_RANGE_MIN, DEFAULT_RANGE_MAX],
    domain: [DEFAULT_RANGE_MIN, DEFAULT_RANGE_MAX],
    step: DEFAULT_STEP,
};
